import { CircularProgress } from "@mui/material";
import {
    Autocomplete,
    AutocompleteOption,
} from "components/Form/components/Autocomplete/Autocomplete";
import { DocsInfoTooltip } from "components/Tooltip/DocsInfoTooltip";
import Tooltip from "components/Tooltip/Tooltip";
import { Wrapper } from "graphql/generated/graphql";
import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./ResourceWrapper.module.scss";
import { actions as wrapperListActions } from "./WrapperList.reducer";
import { getWrappers } from "./WrapperList.selectors";

interface AddWrapperComponentProps {
    createResourceWrapper: (wrapperId: string) => void;
    canAddWrapper: boolean;
    isDisabledWithTooltip: boolean;
    isListLoading?: boolean;
}

// The available-wrappers list is small and bounded; fetch the whole set once so
// the standard searchable dropdown is fully populated and client-side filtered.
const wrapperListQuery = {
    query: `query ($offset: Int, $order: String, $limit: Int){
        wrapper_plain_list(offset: $offset, order: $order, limit: $limit)  {
            id
            name
            input_schema
        }
    }`,
    variables: { offset: 0, order: "+name", limit: 500 },
};

export const AddWrapperComponent = ({
    createResourceWrapper,
    canAddWrapper,
    isDisabledWithTooltip,
    isListLoading,
}: AddWrapperComponentProps) => {
    const dispatch = useDispatch();

    const wrapperList: Wrapper[] = useSelector(getWrappers);

    const isDisabled = !canAddWrapper || isDisabledWithTooltip;

    // Fetch the available wrappers so the picker is populated when opened.
    // Routed through the dedicated graphqlPost-based saga (POST) — the generic
    // GET Listing saga is rejected by the backend (GraphQL is POST-only), which
    // is what left the old picker empty.
    const fetchWrappers = useCallback(
        () =>
            new Promise((resolve, reject) => {
                dispatch({
                    promise: { resolve, reject },
                    type: wrapperListActions.FETCH_LISTING_WRAPPER_LIST,
                    params: wrapperListQuery,
                });
            }).catch((error) => error),
        [dispatch]
    );

    useEffect(() => {
        fetchWrappers();
    }, [fetchWrappers]);

    const wrapperOptions: AutocompleteOption[] = useMemo(
        () =>
            (wrapperList ?? []).map((wrapper) => ({
                value: wrapper.id,
                label: wrapper.name,
            })),
        [wrapperList]
    );

    // Selecting a wrapper adds it; there is no persistent selection, so the
    // control always returns to its "Add wrapper" placeholder (value stays null).
    const onChange = useCallback(
        (wrapperId: string) => {
            if (wrapperId) {
                createResourceWrapper(wrapperId);
            }
        },
        [createResourceWrapper]
    );

    const picker = (
        // pointer-events:none blocks opening the dropdown while disabled (schedule
        // enabled / new record / an unsaved wrapper is still being added); the
        // standard Autocomplete also renders greyed via isDisabled.
        <div className={isDisabled ? styles.disabledAddWrapperButton : undefined}>
            <Autocomplete
                name="add-wrapper"
                value={null}
                onChange={onChange}
                options={{ list: wrapperOptions }}
                isDisabled={isDisabled}
                additionalProps={{
                    clearable: false,
                    fieldWidth: 280,
                    placeholder: "Add wrapper",
                }}
            />
        </div>
    );

    return (
        <div className="addWrapperContainer">
            {isDisabledWithTooltip ? (
                <Tooltip overlay="To add new wrapper, save newly added first">{picker}</Tooltip>
            ) : (
                picker
            )}
            <DocsInfoTooltip title={"Add wrapper"} urlPath={"/docs/wrappers"} />
            {isListLoading && <CircularProgress disableShrink className="spinner" size={20} />}
        </div>
    );
};
